import type { ISODate } from '@mathflat/shared/@types/date'
import type { ZeroValue } from '@mathflat/shared/@types/number'
import type { EmptyString } from '@mathflat/shared/@types/string'
import type { ValueOf } from '@mathflat/shared/@types/utilityTypes'
import { format } from 'date-fns'

import { WorkbookDomain } from '~/@entities/(Content)/Workbook/domain.ts'
import type { AcademyDomain } from '~/@entities/Academy/module.ts'
import type { CURRICULUM_CONCEPT_TYPE } from '~/@entities/Curriculum/constant'
import type { DesignTemplateResponse } from '~/@entities/DesignTemplate/api'
import {
  type EditableImageCoverDataItem,
  type TextCoverDataItem,
  ToLocaleStringCoverDataItem,
} from '~/@entities/DesignTemplate/CoverData/dto'
import { CoverData } from '~/@entities/DesignTemplate/CoverData/dto'
import type { DesignTemplate } from '~/@entities/DesignTemplate/dto'
import type { Entity as OrderEntity } from '~/@entities/PODMall/Order/dto'
import type { Entity as ProductEntity } from '~/@entities/PODMall/Product/dto'
import type { Entity as ProductDetailEntity } from '~/@entities/PODMall/ProductDetail/dto'
import type { AllGradeSubject, SchoolType, Semester } from '~/@entities/SchoolSystem/schema.ts'
import { SchoolSystem } from '~/@entities/SchoolSystem/SchoolSystem'
import type { TeacherSchema } from '~/@entities/Teacher/schema'

import type { ContentLevel, PARTITION_TYPE } from '../module'
import type { _WorkbookApi, WorkbookApi } from './api'
// private type으로 접근하지 마세요
type _WorkbookType = ValueOf<typeof WorkbookDomain.TYPE>
type _ZeroPage = ZeroValue | null | undefined
type _SignatureType = WorkbookDomain.SignatureType
type _CommonWorkbookGetters =
  | '한글학년학기과목'
  | '한글학년학기과목개정'
  | 'uiWorkbookTitle'
  | 'formattedCreateDatetime'
type _CurriculumConceptType = ValueOf<typeof CURRICULUM_CONCEPT_TYPE>

class _WorkbookCommon {
  id: number
  type: _WorkbookType
  schoolType: SchoolType
  grade: AllGradeSubject
  semester: Semester
  title: string
  subtitle: string | null
  fulltitle: string
  autoScored: boolean
  coverType: number | null // TODO: 리터럴일 수 있음.
  partitionType: keyof typeof PARTITION_TYPE
  createDatetime: Date
  revision: 'CURRICULUM_15' | 'CURRICULUM_22' // 개정 - 교육과정 15, 22
  thumbnailImageUrl: string

  constructor(
    params: Omit<
      _WorkbookCommon,
      'createDatetime' | 'formattedCreateDatetime' | _CommonWorkbookGetters
    > & {
      createDatetime: ISODate | Date | string
    },
  ) {
    this.id = params.id
    this.type = params.type
    this.schoolType = params.schoolType
    this.grade = params.grade
    this.semester = params.semester
    this.title = params.title
    this.subtitle = params.subtitle
    this.fulltitle = params.fulltitle
    this.autoScored = params.autoScored
    this.coverType = params.coverType
    this.partitionType = params.partitionType
    this.createDatetime = new Date(params.createDatetime)
    this.revision = params.revision
    this.thumbnailImageUrl = params.thumbnailImageUrl
  }

  static getKoreanGradeSemesterSubject({
    grade,
    semester,
    schoolType,
    revision,
    workbookType,
  }: {
    grade: AllGradeSubject
    semester: Semester
    schoolType: SchoolType
    workbookType: _WorkbookType
    revision?: _WorkbookCommon['revision']
  }) {
    const { koSchoolType, koSubject } = SchoolSystem.getKoreanCommonGradeSemester({
      grade,
      semester,
      schoolType,
    })
    if (schoolType === 'MIDDLE' && workbookType === 'SCHOOL') {
      return `${koSchoolType}${grade}`
    }

    if (koSchoolType === '고') {
      return koSubject
    }
    return revision
      ? `${koSchoolType} ${grade}-${semester}`
      : `${koSchoolType} ${grade}-${semester} ${revision === 'CURRICULUM_22' ? '(22개정)' : ''}`
  }

  get 한글학년학기과목() {
    return _WorkbookCommon.getKoreanGradeSemesterSubject({
      grade: this.grade,
      semester: this.semester,
      schoolType: this.schoolType,
      workbookType: this.type,
    })
  }

  get 한글학년학기과목개정() {
    return _WorkbookCommon.getKoreanGradeSemesterSubject({
      grade: this.grade,
      semester: this.semester,
      schoolType: this.schoolType,
      workbookType: this.type,
      revision: this.revision,
    })
  }

  get uiWorkbookTitle() {
    return `${this.title} ${this.subtitle ? `- ${this.subtitle}` : ''}`
  }

  get formattedCreateDatetime() {
    return format(this.createDatetime, 'yy.MM.dd')
  }
}

export namespace Entity {
  export type CustomWorkbookCoverDataT = CustomSignatureWorkbookCoverDataT

  export class CustomWorkbook<
    T extends WorkbookApi.CustomWorkbookType = WorkbookApi.CustomWorkbookType,
  > extends _WorkbookCommon {
    override type: Extract<_WorkbookType, typeof WorkbookDomain.TYPE.내교재>
    headtitle: string
    minPage: number
    maxPage: number
    // page: number
    answerPage: number
    coverData: CoverData<CustomWorkbookCoverDataT> | null = null
    featureType: WorkbookDomain.CustomSignatureFeatureType
    customWorkbookType: T
    coverPages: WorkbookApi.GetCustomWorkbookById.Response['coverPages']
    innerDesignTemplateId: WorkbookApi.PostInnerDesignTemplate.Response['id'] = 1
    designTemplateId: DesignTemplate['id'] | null
    productId: ProductEntity.SignatureWorkbookProduct['id']
    // thumbnailImageKey: string
    problemColorPrice: ProductDetailEntity.ProductDetail['problemAnswerColorPrice']
    problemBwPrice: ProductDetailEntity.ProductDetail['problemAnswerBwPrice']
    problemAnswerBwPrice: ProductDetailEntity.ProductDetail['problemAnswerBwPrice']
    problemAnswerColorPrice: ProductDetailEntity.ProductDetail['problemAnswerColorPrice']
    productDiscountRate: ProductDetailEntity.ProductDetail['productDiscountRate']
    problemColorDiscountedPrice: ProductDetailEntity.ProductDetail['problemColorDiscountedPrice']
    problemBwDiscountedPrice: ProductDetailEntity.ProductDetail['problemBwDiscountedPrice']
    problemAnswerColorDiscountedPrice: ProductDetailEntity.ProductDetail['problemAnswerColorDiscountedPrice']
    problemAnswerBwDiscountedPrice: ProductDetailEntity.ProductDetail['problemAnswerBwDiscountedPrice']
    orderDatetime: OrderEntity.DirectOrder['orderDatetime'] | null
    pdfStatus: 'FAIL' | 'SUCCESS' | 'GENERATING'
    // pdfUrl: string | null
    showLittleChapterTheory: boolean
    includeConceptNameInPdf: WorkbookApi.PostPutCustomWorkbook.SchoolTestPdfOption['includeConceptNameInPdf'] // 학교별기출PDF옵션 개념명포함
    includeAnswerRateInPdf: WorkbookApi.PostPutCustomWorkbook.SchoolTestPdfOption['includeAnswerRateInPdf'] // 학교별기출PDF옵션 정답률포함
    includeLevelInPdf: WorkbookApi.PostPutCustomWorkbook.SchoolTestPdfOption['includeLevelInPdf'] // 학교별기출PDF옵션 난이도포함
    includeTagTopInPdf: WorkbookApi.PostPutCustomWorkbook.SchoolTestPdfOption['includeTagTopInPdf'] // 학교별기출PDF옵션 태그포함
    // curriculumConceptType: _CurriculumConceptType

    originWorkbook: {
      // 연관된 시중 교재 정보
      id: number
      fulltitle: string
      securingType: WorkbookDomain.SecuringType
      type: WorkbookDomain.Type
    } | null

    constructor(
      params:
        | (Omit<CustomWorkbook<T>, 'createDatetime' | 'orderDatetime' | _CommonWorkbookGetters> & {
            createDatetime: ISODate | Date | string
            orderDatetime: ISODate | Date | null | string
          })
        | (Omit<CustomWorkbook<T>, 'createDatetime' | 'orderDatetime' | _CommonWorkbookGetters> & {
            createDatetime: ISODate | Date | string
            orderDatetime: ISODate | Date | null | string
            coverData: DesignTemplateResponse<CustomWorkbookCoverDataT>['coverData'] | null
          }),
    ) {
      super(params)
      this.type = params.type
      this.headtitle = params.headtitle
      this.subtitle = params.subtitle
      this.minPage = params.minPage
      this.maxPage = params.maxPage
      // this.page = params.page
      this.answerPage = (params.answerPage ?? params.answerPage)!
      this.innerDesignTemplateId = params.innerDesignTemplateId
      this.designTemplateId = params.designTemplateId
      this.productId = params.productId
      this.featureType = params.featureType
      // this.thumbnailImageKey = params.thumbnailImageKey
      this.problemColorPrice = params.problemColorPrice
      this.problemBwPrice = params.problemBwPrice
      this.problemAnswerBwPrice = params.problemAnswerBwPrice
      this.problemAnswerColorPrice = params.problemAnswerColorPrice
      this.orderDatetime = params.orderDatetime ? new Date(params.orderDatetime) : null
      // this.pdfUrl = params.pdfUrl
      this.pdfStatus = params.pdfStatus
      // 요금제별 할인율
      this.productDiscountRate = params.productDiscountRate
      //  할인된 가격
      this.problemColorDiscountedPrice = params.problemColorDiscountedPrice
      this.problemBwDiscountedPrice = params.problemBwDiscountedPrice
      this.problemAnswerColorDiscountedPrice = params.problemAnswerColorDiscountedPrice
      this.problemAnswerBwDiscountedPrice = params.problemAnswerBwDiscountedPrice

      this.customWorkbookType = params.customWorkbookType
      this.coverPages = params.coverPages
      this.includeConceptNameInPdf = params.includeConceptNameInPdf
      this.includeAnswerRateInPdf = params.includeAnswerRateInPdf
      this.includeLevelInPdf = params.includeLevelInPdf
      this.includeTagTopInPdf = params.includeTagTopInPdf
      this.showLittleChapterTheory = params.showLittleChapterTheory
      // this.curriculumConceptType = params.curriculumConceptType
      this.originWorkbook = params.originWorkbook || null
      if (params.coverData) {
        this.coverData = new CoverData(params.coverData)

        // 교재 가격 배포 전 생성된 교재들에 대해 가격정보가 없는 경우 default 값을 설정. (임시코드)
        if (this.coverData && !this.coverData.preAssignedCoverData.salesPrice) {
          this.coverData.preAssignedCoverData.salesPrice =
            new ToLocaleStringCoverDataItem<'salesPrice'>(tempSalesPriceConstructorParams)
        }
      }
    }
  }

  export class PublicWorkbook extends _WorkbookCommon {
    override type: Extract<_WorkbookType, typeof WorkbookDomain.TYPE.시중교재>
    page: _ZeroPage
    level: ContentLevel
    headtitle: EmptyString
    edition: EmptyString
    publisher: string
    pdfUrl: null
    answerPdfUrl: null
    minPage: _ZeroPage
    maxPage: _ZeroPage
    answerPage: _ZeroPage
    pairX: 0 | 1 | 2 | 3 | null
    pairFlag: boolean
    conceptFlag: boolean
    /**
     * 시중교재 확보 형태
     * - 시중교재는 교재 데이터를 어떤 방식으로 확보했는지에 따라 1군과 2군으로 분류함
     *   - 1군: `지원해야하는 시중교재 콘팀이 판단 → 시중교재 구매 > 빠른정답 입력 > CMS 업로드`
     *   - 2군: `교재 수급 → 스캔대행 → OCR 분석 → 유사문제 추출 → DB 등록` (AI가 유형분류)
     */
    securingType: WorkbookDomain.SecuringType
    answerConfirmed: boolean

    constructor(
      params: Omit<PublicWorkbook, 'createDatetime' | _CommonWorkbookGetters> & {
        createDatetime: ISODate | Date
        securingType: WorkbookDomain.SecuringType
        answerConfirmed: boolean
      },
    ) {
      super(params)
      this.type = params.type
      this.level = params.level
      this.page = params.page
      this.headtitle = params.headtitle
      this.edition = params.edition
      this.publisher = params.publisher
      this.pdfUrl = params.pdfUrl
      this.answerPdfUrl = params.answerPdfUrl
      this.minPage = params.minPage
      this.maxPage = params.maxPage
      this.answerPage = params.answerPage
      this.pairX = params.pairX
      this.pairFlag = params.pairFlag
      this.conceptFlag = params.conceptFlag
      this.securingType = params.securingType
      this.answerConfirmed = params.answerConfirmed
    }
    get korAnswerConfirmed() {
      return this.answerConfirmed ? '지원' : '미지원'
    }
  }

  export class SchoolWorkbook extends _WorkbookCommon {
    override type: Extract<_WorkbookType, typeof WorkbookDomain.TYPE.교과서>
    page: _ZeroPage
    level: ContentLevel
    headtitle: EmptyString
    edition: string
    publisher: string
    pdfUrl: null
    answerPdfUrl: null
    minPage: _ZeroPage
    maxPage: _ZeroPage
    answerPage: _ZeroPage
    teacherId: TeacherSchema.Id
    pairX: 0 | 1 | 2 | 3 | null
    pairFlag: boolean
    assigned: boolean | null // TODO: deprecated
    conceptFlag: boolean

    constructor(
      params: Omit<SchoolWorkbook, 'createDatetime' | _CommonWorkbookGetters> & {
        createDatetime: ISODate | Date
      },
    ) {
      super(params)
      this.type = params.type
      this.page = params.page
      this.level = params.level
      this.headtitle = params.headtitle
      this.edition = params.edition
      this.publisher = params.publisher
      this.pdfUrl = params.pdfUrl
      this.answerPdfUrl = params.answerPdfUrl
      this.minPage = params.minPage
      this.maxPage = params.maxPage
      this.answerPage = params.answerPage
      this.teacherId = params.teacherId
      this.pairX = params.pairX
      this.pairFlag = params.pairFlag
      this.assigned = params.assigned // TODO: deprecated
      this.conceptFlag = params.conceptFlag
    }
  }

  export class SignatureWorkbook extends _WorkbookCommon {
    override type: Extract<_WorkbookType, typeof WorkbookDomain.TYPE.시그니처교재>
    page: _ZeroPage
    level: ContentLevel
    headtitle: string
    featureType: WorkbookDomain.CustomSignatureFeatureType
    edition: EmptyString
    publisher: string
    pdfUrl: string
    answerPdfUrl: string
    minPage: _ZeroPage
    maxPage: _ZeroPage
    answerPage: _ZeroPage
    pairX: 0 | 1 | 2 | 3 | null
    pairFlag: boolean
    signatureType: _SignatureType
    signatureWorkbookFulltitle: string
    productId: number
    assignmentFlag: boolean // 출제가능여부 (출제 되었다가 아님)
    conceptFlag: boolean

    constructor(
      params: Omit<SignatureWorkbook, 'createDatetime' | _CommonWorkbookGetters> & {
        createDatetime: ISODate | Date
      },
    ) {
      super(params)
      this.page = params.page
      this.type = params.type
      this.level = params.level
      this.headtitle = params.headtitle
      this.featureType = params.featureType
      this.edition = params.edition
      this.publisher = params.publisher
      this.pdfUrl = params.pdfUrl
      this.answerPdfUrl = params.answerPdfUrl
      this.minPage = params.minPage
      this.maxPage = params.maxPage
      this.answerPage = params.answerPage
      this.pairX = params.pairX
      this.pairFlag = params.pairFlag
      this.signatureType = params.signatureType
      this.productId = params.productId
      this.signatureWorkbookFulltitle = params.signatureWorkbookFulltitle
      this.assignmentFlag = params.assignmentFlag
      this.conceptFlag = params.conceptFlag
    }
  }

  type _CustomSignatureWorkbookCoverDataT = {
    headtitle: TextCoverDataItem<'headtitle'> // 대제목이 아니라 학년/학기/과목 이라는 충격적 사실
    subtitle: TextCoverDataItem<'subtitle'> | null
    title: TextCoverDataItem<'title'> // 대제목
    academyName: TextCoverDataItem<'academyName'> | null
    studentName: TextCoverDataItem<'studentName'>
    sideTitle: TextCoverDataItem<'sideTitle'> // value가 empty일 수 있음
    backTopText: TextCoverDataItem<'backTopText'>
    backBottomText: TextCoverDataItem<'backBottomText'>
    frontLogoImage: EditableImageCoverDataItem | null
    backLogoImage: EditableImageCoverDataItem | null
    salesPrice: ToLocaleStringCoverDataItem<'salesPrice'>
  }

  export type CustomSignatureWorkbookCoverDataT = _CustomSignatureWorkbookCoverDataT

  const tempSalesPriceConstructorParams: ConstructorParameters<
    typeof ToLocaleStringCoverDataItem<'salesPrice'>
  >[0] = {
    type: 'TO_LOCALE_STRING',
    props: {
      readOnly: true,
      prefixValue: '정가: ',
      renderProps: {
        x: 2,
        y: 767,
        height: 12,
        fontSize: 10,
        maxWidth: 100,
        position: 'bottom-right',
        fontColor: '#111',
        fontFamily: 'Pretendard-Medium',
        transform: 'rotate(90 52 773)',
      },
      suffixValue: '원',
      defaultValue: 10000,
    },
    id: 'salesPrice',
    order: 10,
  }
  export class CustomSignatureWorkbook extends _WorkbookCommon {
    override type: Extract<_WorkbookType, typeof WorkbookDomain.TYPE.커스텀시그니처교재>
    page: _ZeroPage
    level: ContentLevel
    headtitle: string
    featureType: WorkbookDomain.CustomSignatureFeatureType
    edition: EmptyString
    publisher: string
    pdfUrl: string
    answerPdfUrl: string
    minPage: _ZeroPage
    maxPage: _ZeroPage
    answerPage: _ZeroPage
    pairX: 0 | 1 | 2 | 3 | null
    pairFlag: boolean
    signatureType: _SignatureType
    thumbnailImageKey: string | null

    productId: number
    productStatus: 'ACTIVE' | 'INACTIVE'
    designTemplateId: DesignTemplate['id'] | null
    solutionPdfUrl: string
    academyId: AcademyDomain.Id
    signatureWorkbookFulltitle: string
    signatureWorkbookId: number
    coverData: CoverData<CustomSignatureWorkbookCoverDataT> | null = null

    orderDatetime: Date | null
    orderStatus: 'PAYMENT_COMPLETED' | 'IN_PRODUCTION' | 'DELIVERY_COMPLETED' | 'ORDER_CANCEL'
    assignmentFlag: boolean // 출제가능여부 (출제 되었다가 아님)
    conceptFlag: boolean

    constructor({
      coverData,
      ...params
    }:
      | (Omit<
          CustomSignatureWorkbook,
          'createDatetime' | 'orderDatetime' | 'formattedCreateDatetime' | _CommonWorkbookGetters
        > & {
          createDatetime: ISODate | Date
          orderDatetime?: ISODate | Date | null
        })
      | (Omit<
          CustomSignatureWorkbook,
          | 'createDatetime'
          | 'orderDatetime'
          | 'coverData'
          | 'formattedCreateDatetime'
          | _CommonWorkbookGetters
        > & {
          createDatetime: ISODate | Date
          orderDatetime?: ISODate | Date | null
          coverData: DesignTemplateResponse<CustomSignatureWorkbookCoverDataT>['coverData'] | null
        })) {
      super(params)
      this.page = params.page
      this.type = params.type
      this.level = params.level
      this.headtitle = params.headtitle
      this.featureType = params.featureType
      this.edition = params.edition
      this.publisher = params.publisher
      this.pdfUrl = params.pdfUrl
      this.answerPdfUrl = params.answerPdfUrl
      this.minPage = params.minPage
      this.maxPage = params.maxPage
      this.answerPage = params.answerPage
      this.pairX = params.pairX
      this.pairFlag = params.pairFlag
      this.signatureType = params.signatureType
      this.thumbnailImageKey = params.thumbnailImageKey
      this.productId = params.productId
      this.designTemplateId = params.designTemplateId
      this.solutionPdfUrl = params.solutionPdfUrl
      this.academyId = params.academyId
      this.signatureWorkbookFulltitle = params.signatureWorkbookFulltitle
      this.signatureWorkbookId = params.signatureWorkbookId
      this.orderDatetime = params.orderDatetime ? new Date(params.orderDatetime) : null
      this.orderStatus = params.orderStatus
      this.productStatus = params.productStatus
      this.assignmentFlag = params.assignmentFlag
      this.conceptFlag = params.conceptFlag

      this.coverData = coverData
        ? new CoverData<CustomSignatureWorkbookCoverDataT>(coverData)
        : null

      // 교재 가격 배포 전 생성된 교재들에 대해 가격정보가 없는 경우 default 값을 설정. (임시코드)
      if (this.coverData && !this.coverData.preAssignedCoverData.salesPrice) {
        this.coverData.preAssignedCoverData.salesPrice =
          new ToLocaleStringCoverDataItem<'salesPrice'>(tempSalesPriceConstructorParams)
      }
    }
  }

  export class AcademyGroupWorkbook extends _WorkbookCommon {
    override type: Extract<_WorkbookType, typeof WorkbookDomain.TYPE.학원그룹교재>
    page: _ZeroPage
    level: ContentLevel
    headtitle: string
    edition: EmptyString
    publisher: EmptyString
    pdfUrl: null
    answerPdfUrl: null
    minPage: _ZeroPage
    maxPage: _ZeroPage
    answerPage: _ZeroPage
    conceptFlag: boolean
    securingType: null
    pairX: 0 | 1 | 2 | 3 | null
    pairFlag: boolean
    academyGroupId: string
    academyGroupType: WorkbookDomain.AcademyGroupType
    subtype: string
    override thumbnailImageUrl: string
    quickAnswerPdfUrl: string
    additionalFields: Record<string, unknown>

    constructor(
      params: Omit<AcademyGroupWorkbook, 'createDatetime' | _CommonWorkbookGetters> & {
        createDatetime: ISODate | Date
      },
    ) {
      super(params)
      this.type = params.type
      this.page = params.page
      this.level = params.level
      this.headtitle = params.headtitle
      this.edition = params.edition
      this.publisher = params.publisher
      this.pdfUrl = params.pdfUrl
      this.answerPdfUrl = params.answerPdfUrl
      this.minPage = params.minPage
      this.maxPage = params.maxPage
      this.answerPage = params.answerPage
      this.conceptFlag = params.conceptFlag
      this.securingType = params.securingType
      this.pairX = params.pairX
      this.pairFlag = params.pairFlag
      this.academyGroupId = params.academyGroupId
      this.academyGroupType = params.academyGroupType
      this.subtype = params.subtype
      this.thumbnailImageUrl = params.thumbnailImageUrl
      this.quickAnswerPdfUrl = params.quickAnswerPdfUrl
      this.additionalFields = params.additionalFields
    }
  }

  // * Workbook *
  export type Workbook<T extends WorkbookDomain.Type = WorkbookDomain.Type> =
    T extends WorkbookDomain.TYPE['교과서']
      ? SchoolWorkbook
      : T extends WorkbookDomain.TYPE['시중교재']
        ? PublicWorkbook
        : T extends WorkbookDomain.TYPE['내교재']
          ? CustomWorkbook
          : T extends WorkbookDomain.TYPE['시그니처교재']
            ? SignatureWorkbook
            : T extends WorkbookDomain.TYPE['커스텀시그니처교재']
              ? CustomSignatureWorkbook
              : T extends WorkbookDomain.TYPE['학원그룹교재']
                ? AcademyGroupWorkbook
                : never

  export type SupportedWorkbookType = Extract<
    WorkbookDomain.Type,
    | WorkbookDomain.TYPE['커스텀시그니처교재']
    | WorkbookDomain.TYPE['학원그룹교재']
    | WorkbookDomain.TYPE['시중교재']
    | WorkbookDomain.TYPE['교과서']
  >

  export type SupportedWorkbook<T extends SupportedWorkbookType = SupportedWorkbookType> =
    T extends WorkbookDomain.TYPE['교과서']
      ? SchoolWorkbook
      : T extends WorkbookDomain.TYPE['시중교재']
        ? PublicWorkbook
        : T extends WorkbookDomain.TYPE['커스텀시그니처교재']
          ? CustomSignatureWorkbook
          : T extends WorkbookDomain.TYPE['학원그룹교재']
            ? AcademyGroupWorkbook
            : never

  export function createWorkbook<T extends WorkbookDomain.Type>(
    workbook: Workbook<T>,
  ): Workbook<T> {
    switch (workbook.type) {
      case WorkbookDomain.TYPE.교과서:
        return new SchoolWorkbook(workbook) as Workbook<T>
      case WorkbookDomain.TYPE.시중교재:
        return new PublicWorkbook(workbook) as Workbook<T>
      case WorkbookDomain.TYPE.내교재:
        return new CustomWorkbook(workbook) as Workbook<T>
      case WorkbookDomain.TYPE.시그니처교재:
        return new SignatureWorkbook(workbook) as Workbook<T>
      case WorkbookDomain.TYPE.커스텀시그니처교재:
        return new CustomSignatureWorkbook(workbook) as Workbook<T>
      case WorkbookDomain.TYPE.학원그룹교재:
        return new AcademyGroupWorkbook(workbook) as Workbook<T>
    }
  }

  export class WorkbookResponse<T extends WorkbookDomain.Type = WorkbookDomain.Type> {
    workbook: Workbook<T>
    assigned: boolean | null
    lastAssignDateTime: Date | null
    lastUsePageNumber: number | null
    list: WorkbookDomain.AssignedStudent[]

    constructor({
      assigned,
      lastAssignDateTime,
      lastUsePageNumber,
      list,
      ...params
    }: _WorkbookApi.Response.GetWorkbook<T> | Omit<WorkbookResponse<T>, 'id'>) {
      this.workbook =
        'workbook' in params ? params.workbook : createWorkbook(params as unknown as Workbook<T>)
      this.assigned = assigned
      this.lastAssignDateTime = lastAssignDateTime ? new Date(lastAssignDateTime) : null
      this.lastUsePageNumber = lastUsePageNumber
      this.list = list
    }

    get id() {
      return this.workbook.id
    }
  }
}
